import type { Item, Transfer, TransferCreate } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey } from '../api';
import { StockNav } from '../components/StockNav';
import { Empty, ErrorBox, QueryView } from '../components/ui';
import { formatQty, formatStamp } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { useSite } from '../sites';
import { ItemPicker } from './ReceivePage';

type DraftLine = { item: Item; qty: string };

/** Stock moving between sites: draft, send (it leaves the lots), receive (it becomes lots there, and an order when another company). */
export function TransfersPage() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const { sites, siteId, hub } = useSite();
  const [showNew, setShowNew] = useState(false);
  const list = useQuery({ queryKey: qk.transfers(siteId ?? ''), queryFn: ({ signal }) => api.transfers(siteId ? { site: siteId } : {}, signal) });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['transfers'] });
    void qc.invalidateQueries({ queryKey: ['stock'] });
  };
  const act = useMutation({
    mutationFn: (v: { id: string; action: 'send' | 'receive' | 'cancel'; short?: { lineId: string; qtyReceived: number }[] }) =>
      v.action === 'send' ? api.sendTransfer(v.id) : v.action === 'cancel' ? api.cancelTransfer(v.id) : api.receiveTransfer(v.id, v.short ? { lines: v.short } : {}),
    onSuccess: invalidate,
  });

  return (
    <div className="stack">
      <section className="stack-sm">
        <h1>{t('transfers')}</h1>
        <StockNav />
        <p className="muted">{t('transfersHint')}</p>
      </section>
      {sites.length < 2 ? (
        <p className="note">{t('oneSiteOnly')}</p>
      ) : showNew ? (
        <NewTransfer
          onDone={() => {
            setShowNew(false);
            invalidate();
          }}
          onCancel={() => setShowNew(false)}
        />
      ) : (
        <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => setShowNew(true)}>
          {t('newTransfer')}
        </button>
      )}
      {act.isError ? <ErrorBox error={act.error} /> : null}
      <QueryView query={list}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('noTransfers')}</Empty>
          ) : (
            <ul className="list" data-testid="transfers">
              {rows.map((tr) => (
                <TransferCard key={tr.id} transfer={tr} busy={act.isPending} onAct={(action, short) => act.mutate({ id: tr.id, action, ...(short ? { short } : {}) })} hubId={hub?.id ?? null} intlLocale={intlLocale} />
              ))}
            </ul>
          )
        }
      </QueryView>
    </div>
  );
}

function TransferCard({ transfer: tr, busy, onAct, intlLocale }: { transfer: Transfer; busy: boolean; onAct: (action: 'send' | 'receive' | 'cancel', short?: { lineId: string; qtyReceived: number }[]) => void; hubId: string | null; intlLocale: string }) {
  const { t } = useLocale();
  const [receiving, setReceiving] = useState(false);
  const [got, setGot] = useState<Record<string, string>>({});
  const statusKey = tr.status === 'draft' ? 'status_draft' : tr.status === 'sent' ? 'status_sent' : tr.status === 'received' ? 'received' : 'status_cancelled';
  const badge = tr.status === 'received' ? 'badge badge-ok' : tr.status === 'cancelled' ? 'badge' : 'badge badge-warn';
  const submitReceive = () => {
    const short = tr.lines.flatMap((l) => {
      const raw = got[l.id];
      if (raw === undefined || raw.trim() === '') return [];
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 && n <= l.qty ? [{ lineId: l.id, qtyReceived: n }] : [];
    });
    onAct('receive', short.length ? short : undefined);
    setReceiving(false);
  };
  return (
    <li className="card stack-sm" data-testid={`transfer-${tr.transferNo}`}>
      <div className="row-title">
        <span>
          #{tr.transferNo} · {tr.fromSiteName} → {tr.toSiteName}
        </span>
        <span className={badge}>{t(statusKey)}</span>
      </div>
      <p className="muted">
        {formatQty(tr.totalPacks)} {t('packs')} · {formatStamp(tr.createdAt, intlLocale)}
        {tr.note ? ` · ${tr.note}` : ''}
      </p>
      <ul className="list plain">
        {tr.lines.map((l) => (
          <li key={l.id} className="row">
            <span className="row-main">
              <span className="row-title">{l.itemName}</span>
              <span className="row-sub">
                {l.lotCode ? `${t('lot')} ${l.lotCode}` : t('oldestLotFirst')}
                {l.toLotCode ? ` → ${l.toLotCode}` : ''}
              </span>
            </span>
            <span className="row-end">
              {receiving ? (
                <input type="number" inputMode="decimal" min={0} max={l.qty} step="any" aria-label={`${t('qtyReceived')} ${l.itemName}`} placeholder={formatQty(l.qty)} value={got[l.id] ?? ''} onChange={(e) => setGot({ ...got, [l.id]: e.target.value })} style={{ width: 90 }} />
              ) : (
                <span className="row-end-value">
                  {l.qtyReceived !== null && l.qtyReceived !== l.qty ? `${formatQty(l.qtyReceived)} / ` : ''}
                  {formatQty(l.qty)}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
      {tr.orderId ? (
        <Link to={`/orders/${tr.orderId}`} className="btn btn-secondary btn-block">
          {t('intercompanyOrder')} #{tr.orderNo}
        </Link>
      ) : null}
      {tr.status === 'draft' ? (
        <div className="two-up">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => onAct('send')}>
            {t('sendTransfer')}
          </button>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => { if (window.confirm(t('cancelTransferConfirm'))) onAct('cancel'); }}>
            {t('cancel')}
          </button>
        </div>
      ) : null}
      {tr.status === 'sent' ? (
        receiving ? (
          <div className="two-up">
            <button type="button" className="btn btn-primary" disabled={busy} onClick={submitReceive}>
              {t('confirmReceived')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setReceiving(false)}>
              {t('cancel')}
            </button>
          </div>
        ) : (
          <div className="two-up">
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => onAct('receive')}>
              {t('receiveAll')}
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setReceiving(true)}>
              {t('receiveShort')}
            </button>
          </div>
        )
      ) : null}
    </li>
  );
}

function NewTransfer({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const { t } = useLocale();
  const { sites, siteId, hub } = useSite();
  const [from, setFrom] = useState(siteId ?? hub?.id ?? '');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [picking, setPicking] = useState(false);
  const attempt = useRef<{ body: string; key: string } | null>(null);
  const save = useMutation({
    mutationFn: (v: { body: TransferCreate; key: string }) => api.createTransfer(v.body, v.key),
    retry: (count, error) => error instanceof ApiError && error.status === 0 && count < 3,
    onSuccess: () => {
      attempt.current = null;
      onDone();
    },
  });
  const valid = to && to !== from && lines.length > 0 && lines.every((l) => Number(l.qty) > 0);
  const submit = () => {
    if (!valid) return;
    const body: TransferCreate = { fromSiteId: from, toSiteId: to, ...(note.trim() ? { note: note.trim() } : {}), lines: lines.map((l) => ({ itemId: l.item.id, qty: Number(l.qty) })) };
    const serialized = JSON.stringify(body);
    if (attempt.current?.body !== serialized) attempt.current = { body: serialized, key: newIdempotencyKey() };
    save.mutate({ body, key: attempt.current.key });
  };
  return (
    <section className="card stack-sm" data-testid="new-transfer">
      <h2>{t('newTransfer')}</h2>
      <div className="two-up">
        <label className="field">
          <span>{t('fromSite')}</span>
          <select value={from} onChange={(e) => setFrom(e.target.value)}>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('toSite')}</span>
          <select value={to} onChange={(e) => setTo(e.target.value)}>
            <option value="">—</option>
            {sites
              .filter((s) => s.id !== from)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
        </label>
      </div>
      <ul className="list plain">
        {lines.map((l, i) => (
          <li key={l.item.id} className="row">
            <span className="row-main">
              <span className="row-title">{l.item.name}</span>
            </span>
            <span className="row-end">
              <input type="number" inputMode="decimal" min={0} step="any" aria-label={`${t('qty')} ${l.item.name}`} value={l.qty} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} style={{ width: 90 }} />
              <button type="button" className="btn btn-ghost" aria-label={`${t('remove')} ${l.item.name}`} onClick={() => setLines(lines.filter((_, j) => j !== i))}>
                ×
              </button>
            </span>
          </li>
        ))}
      </ul>
      {picking ? (
        <ItemPicker
          onPick={(item) => {
            if (!lines.some((l) => l.item.id === item.id)) setLines([...lines, { item, qty: '1' }]);
            setPicking(false);
          }}
          onCancel={() => setPicking(false)}
        />
      ) : (
        <button type="button" className="btn btn-secondary btn-block" onClick={() => setPicking(true)}>
          {t('addItem')}
        </button>
      )}
      <label className="field">
        <span>{t('notes')}</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
      </label>
      {save.isError ? <ErrorBox error={save.error} /> : null}
      <div className="two-up">
        <button type="button" className="btn btn-primary" disabled={!valid || save.isPending} onClick={submit}>
          {t('saveDraft')}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          {t('cancel')}
        </button>
      </div>
    </section>
  );
}
